import React, { FormEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '@codehesion-za/headless';
import { FormikProps } from 'formik';

import {
  Button,
  RadioGroup,
  PaymentAmountField,
  ActivityLoader,
  RecaptchaComponent,
  TextField,
} from '@project/components';
import { cardholderSchema } from './schema';
import { DonationValues } from './types';
import { useGetExchangeCurrencyQuery } from '@project/queries';
import { ROBOT_CHECK_PASS } from '@project/helper';
import { useSessionStorage } from '@project/hooks';

export const CardholderForm: React.FC = () => {
  const navigate = useNavigate();
  const [roboCheck, setRebotCheck] = useSessionStorage(ROBOT_CHECK_PASS, false);
  const getMinAmount = (cardType: string) => {
    switch (cardType) {
      case 'Diamond':
        return 5000;
      case 'Platinum':
        return 2700;
      case 'Gold':
        return 1000;
      case 'Silver':
        return 500;
      case 'Bronze':
        return 270;
      case 'Crystal':
        return 150;
      default:
        return 270;
    }
  };

  const onSubmitForm = (formData: DonationValues) => {
    navigate(
      `/payment?type=${formData.donationType}&amount=${formData.donationAmount}&currency=${formData.donationCurrency}&payment_type=card_holder&tipAmount=${formData.tip}&sponsor_magazine=${formData.sponsor_magazine}&payment_method=${formData.paymentMethod}`,
    );
    return new Promise(() => null);
  };

  const FormComponents = ({
    isSubmitting,
    handleSubmit,
    values,
    isValid,
    setFieldError,
  }: FormikProps<DonationValues>) => {
    const { data, isLoading } = useGetExchangeCurrencyQuery(
      getMinAmount(values.donationType ?? 'Bronze'),
      'usd',
      values.donationCurrency ?? 'usd',
    );

    const isDonationAmountInvalid = useCallback(
      () => Number(values.donationAmount) < (data?.convertedAmount ?? 200),
      [values.donationAmount, data?.convertedAmount, isLoading],
    );

    const _handleSubmit = useCallback(
      (formData: FormEvent<HTMLFormElement>) => {
        if (isDonationAmountInvalid()) {
          setFieldError('donationAmount', 'Must be greater than or equal to minimum amount.');
          return;
        }

        handleSubmit(formData);
      },
      [data, values],
    );

    return (
      <div className="form-styling justify-center">
        <h1 className="mt-5 mb-4 ml-5 text-base">Card Holder</h1>
        <div className="mt-5 ml-5 flex flex-col text-sm">
          <RadioGroup
            name="donationType"
            options={[]}
            id="crystal"
            label="Crystal $150/R2500"
            value="Crystal"
          />
          <RadioGroup
            name="donationType"
            options={[]}
            id="bronze"
            label="Bronze $270/R5000"
            value="Bronze"
          />
          <RadioGroup
            name="donationType"
            options={[]}
            id="silver"
            label="Silver $500/R10000"
            value="Silver"
          />
          <RadioGroup
            name="donationType"
            options={[]}
            id="gold"
            label="Gold $1000/R20000"
            value="Gold"
          />
          <RadioGroup
            name="donationType"
            options={[]}
            id="platinum"
            label="Platinum $2700/R50000"
            value="Platinum"
          />
          <RadioGroup
            name="donationType"
            options={[]}
            id="diamond"
            label="Diamond $5000/R100000"
            value="Diamond"
          />
        </div>

        <div className="w-full">
          <PaymentAmountField
            amountFieldName="donationAmount"
            currencyFieldName="donationCurrency"
            selectedCurrency={values.donationCurrency}
            containerClassNames="mb-0"
            paymentMethod={values.paymentMethod ?? 'debit_credit'}
          />
          {!isLoading && isDonationAmountInvalid() && (
            <p className="text-left text-red-600">
              Minimum donation amount of {values?.donationCurrency}
              {` ${data?.convertedAmount}`} is Required
            </p>
          )}
          <TextField name="tip" label="Parach(optional)" type="number" required={false} />
          <TextField
            name="sponsor_magazine"
            label="Sponsor Magazine(optional)"
            type="number"
            required={false}
          />
          {isLoading && <ActivityLoader isLoading={isLoading} />}
        </div>
        <Button
          isDisabled={isSubmitting || !isValid}
          style="bg-gold text-white font-bold text-sm rounded-md py-3 w-full"
          isLoading={isSubmitting}
          onClick={_handleSubmit}
        >
          Next
        </Button>
      </div>
    );
  };

  return roboCheck ? (
    <Form
      initialValues={{
        donationAmount: 1,
        donationType: 'Bronze',
        donationCurrency: 'USD',
        tip: 0,
        sponsor_magazine: 0,
        paymentMethod: 'debit_credit',
      }}
      onSubmitForm={onSubmitForm}
      render={FormComponents}
      validationSchema={cardholderSchema}
      validateOnMount
      validateOnBlur
      validateOnChange
    />
  ) : (
    <div className="my-2">
      <RecaptchaComponent setRebotCheck={setRebotCheck} />
    </div>
  );
};
